import { z } from "zod";

/**
 * Why a push advanced a ref and built nothing.
 *
 * `advanced_no_deploy` used to be one undifferentiated outcome, which made a
 * misconfigured project indistinguishable from a working one: a project whose
 * only app was never linked to it absorbed pushes for hours and looked exactly
 * like a repository nobody had pushed to. Naming the reason is what separates
 * ordinary development traffic from the two shapes that mean something is
 * wrong.
 *
 * Single source of truth — consumed by:
 *
 *   - `IngestVibeGitPushUseCase`'s `advanced_no_deploy` outcome (the reason it
 *     returns to its caller).
 *   - The `GIT_PUSH_NO_DEPLOY` audit payload's `reason` field.
 *   - `VibeGitReceiveHookController`, which picks a log level and the notice
 *     relayed back to the pusher's terminal per reason.
 *
 * NOT every reason earns a ledger row — see `VIBE_GIT_PUSH_UNEXPECTED_NO_DEPLOY_REASONS`.
 */
export const VIBE_GIT_PUSH_NO_DEPLOY_REASONS = [
  /**
   * The push deleted a branch (git zero-sha on `newSha`). The ref advance
   * records the deletion; there is no commit to build. Never surprising.
   */
  "branch_deleted",
  /**
   * The git project has no app attached at all. Two very different worlds
   * collapse here and the platform cannot tell them apart from the project
   * side: a deliberate standalone code store, and an app that exists but was
   * never linked to its project. The second is the failure this whole event
   * type exists for.
   */
  "no_apps_attached",
  /**
   * Apps are attached, but none of them watches the pushed ref. This is the
   * dominant no-deploy shape in normal use — every feature-branch push and
   * every tag push lands here — so it is deliberately quiet. The audit event's
   * `watchedRefs` is what answers "then what SHOULD I have pushed to?" for the
   * typo case.
   */
  "ref_not_watched",
  /**
   * Apps matched the pushed ref and every single deploy trigger was refused
   * (a cost-safety suspension, a missing git project, a persistence fault).
   * The push is recorded; the platform declined to build it.
   */
  "all_deploys_refused"
] as const;

export type VibeGitPushNoDeployReason = (typeof VIBE_GIT_PUSH_NO_DEPLOY_REASONS)[number];

export const VibeGitPushNoDeployReasonSchema = z.enum(VIBE_GIT_PUSH_NO_DEPLOY_REASONS);

/**
 * The reasons that earn a `GIT_PUSH_NO_DEPLOY` audit row, because a human
 * plausibly expected a deploy and did not get one.
 *
 * The two excluded reasons are ordinary development traffic — a repository
 * with any branch discipline at all produces far more `ref_not_watched` pushes
 * than deploying ones — and writing a row for each would bury the event that
 * matters under its own repetitions. Same reasoning as `APP_EDGE_UNROUTED`
 * writing on the transition into UNROUTED rather than on every probe.
 */
export const VIBE_GIT_PUSH_UNEXPECTED_NO_DEPLOY_REASONS = [
  "no_apps_attached",
  "all_deploys_refused"
] as const satisfies readonly VibeGitPushNoDeployReason[];

export type VibeGitPushUnexpectedNoDeployReason =
  (typeof VIBE_GIT_PUSH_UNEXPECTED_NO_DEPLOY_REASONS)[number];

/**
 * Compile-time exhaustiveness check. Every reason must be EITHER audited above
 * OR explicitly listed as ordinary traffic here. Adding a new reason without
 * deciding which it is fails the `never` constraint — the only ways out are to
 * add it to the audited tuple or to name it in the `Exclude` clause, so a new
 * silent no-deploy shape cannot be introduced by omission.
 */
type _UncategorizedReason = Exclude<
  VibeGitPushNoDeployReason,
  VibeGitPushUnexpectedNoDeployReason | "branch_deleted" | "ref_not_watched"
>;
const _reasonCheck: _UncategorizedReason extends never ? true : never = true;
void _reasonCheck;

/**
 * True when this no-deploy reason earns a ledger row. Narrows, so the caller
 * can hand the reason straight to the audit payload without re-asserting.
 */
export function isUnexpectedVibeGitPushNoDeploy(
  reason: VibeGitPushNoDeployReason
): reason is VibeGitPushUnexpectedNoDeployReason {
  return (VIBE_GIT_PUSH_UNEXPECTED_NO_DEPLOY_REASONS as readonly string[]).includes(reason);
}
